import React, { useState, useEffect } from "react";
import api from "../../api";
import Button from "../Button";

function ToggleSwitch({ checked, onChange, disabled, label }) {
  return (
    <div
      className={`flex items-center justify-between p-3 bg-white rounded-lg ${
        disabled ? "opacity-50" : ""
      }`}
    >
      <span className="text-sm font-medium text-gray-700">{label}</span>

      <label
        className={`relative inline-flex items-center ${
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[#4CAF50] transition-all"></div>
        <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full peer-checked:translate-x-5 transition"></div>
      </label>
    </div>
  );
}

export default function ProvinceModal({ province, onClose, onSuccess }) {
  const isEdit = !!province;

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [isCustomerAvailable, setIsCustomerAvailable] = useState(true);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteProvinceLoading, setDeleteProvinceLoading] = useState(false);
  const [deletingCityId, setDeletingCityId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (province) {
      setName(province.name || "");
      setCode(province.code || "");
      setIsActive(province.is_active ?? true);
      setIsCustomerAvailable(province.is_customer_available ?? true);
      setCities(
        (province.cities || []).map((city) => ({
          ...city,
          is_active: city.is_active ?? true,
          is_customer_available: city.is_customer_available ?? true,
        }))
      );
    } else {
      setName("");
      setCode("");
      setIsActive(true);
      setIsCustomerAvailable(true);
      setCities([]);
    }

    setError("");
  }, [province]);

  const handleAddCity = () => {
    setCities((prev) => [
      ...prev,
      {
        name: "",
        code: "",
        latitude: "",
        longitude: "",
        zoom: "",
        is_active: true,
        is_customer_available: true,
      },
    ]);
  };

  const handleRemoveCity = async (index) => {
    const city = cities[index];
    const cityName = city?.name?.trim() || `ردیف ${index + 1}`;
    const confirmed = window.confirm(`آیا از حذف شهر "${cityName}" مطمئن هستید؟`);

    if (!confirmed) {
      return;
    }

    setError("");

    // شهرهای جدید که هنوز ذخیره نشده‌اند، فقط از state حذف می‌شوند.
    if (!city?.id) {
      setCities((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    setDeletingCityId(city.id);

    try {
      await api.delete(`/accounts/cities/${city.id}/`);
      setCities((prev) => prev.filter((item) => item.id !== city.id));
    } catch (err) {
      console.error("خطا در حذف شهر:", err);
      setError(
        err.response?.data?.detail ||
          "خطا در حذف شهر. ممکن است این شهر در آدرس‌ها استفاده شده باشد."
      );
    } finally {
      setDeletingCityId(null);
    }
  };

  const handleCityChange = (index, field, value) => {
    setCities((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const validateForm = () => {
    if (!name.trim()) {
      setError("نام استان الزامی است");
      return false;
    }

    if (!code.trim()) {
      setError("کد استان الزامی است");
      return false;
    }

    for (let i = 0; i < cities.length; i += 1) {
      if (!cities[i].name.trim()) {
        setError(`نام شهر در ردیف ${i + 1} الزامی است`);
        return false;
      }

      if (!cities[i].code?.trim()) {
        setError(`کد شهر در ردیف ${i + 1} الزامی است`);
        return false;
      }
    }

    return true;
  };

  const handleDeleteProvince = async () => {
    if (!province?.id) {
      return;
    }

    const confirmed = window.confirm(
      `آیا از حذف استان "${province.name}" و شهرهای وابسته به آن مطمئن هستید؟`
    );

    if (!confirmed) {
      return;
    }

    setDeleteProvinceLoading(true);
    setError("");

    try {
      await api.delete(`/accounts/provinces/${province.id}/`);
      onSuccess();
      onClose();
    } catch (err) {
      console.error("خطا در حذف استان:", err);
      setError(
        err.response?.data?.detail ||
          "خطا در حذف استان. لطفا مجددا تلاش کنید."
      );
    } finally {
      setDeleteProvinceLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      let provinceId;

      if (isEdit) {
        await api.put(`/accounts/provinces/${province.id}/`, {
          name: name.trim(),
          code: code.trim(),
          is_active: isActive,
          is_customer_available: isCustomerAvailable,
        });
        provinceId = province.id;
      } else {
        const res = await api.post("/accounts/provinces/", {
          name: name.trim(),
          code: code.trim(),
          is_active: isActive,
          is_customer_available: isCustomerAvailable,
        });
        provinceId = res.data.id;
      }

      for (const city of cities) {
        const payload = {
          province: provinceId,
          name: city.name.trim(),
          code: city.code.trim(),
          latitude: city.latitude,
          longitude: city.longitude,
          zoom: city.zoom,
          is_active: city.is_active,
          is_customer_available: city.is_customer_available ?? true,
        };

        if (city.id) {
          await api.put(`/accounts/cities/${city.id}/`, payload);
        } else {
          await api.post("/accounts/cities/", payload);
        }
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error("خطا در ذخیره اطلاعات:", err);
      setError(
        err.response?.data?.detail ||
          "خطا در ذخیره اطلاعات. لطفا مجددا تلاش کنید."
      );
    } finally {
      setLoading(false);
    }
  };


  const isBusy = loading || deleteProvinceLoading || deletingCityId !== null;

  return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="relative bg-white w-full max-w-6xl rounded-xl p-6 shadow-xl mx-4 max-h-[90vh] overflow-y-auto">
          {/* دکمه بستن گوشه بالا */}
          <button
              type="button"
              onClick={onClose}
              className="absolute top-4 left-4 text-gray-500 hover:text-gray-700 transition"
              aria-label="بستن"
              disabled={isBusy}
          >
            <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
              <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <div className="flex items-center justify-between mb-4 gap-3 pr-10">
            <h2 className="text-xl font-bold text-right">
              {isEdit ? "ویرایش استان" : "افزودن استان جدید"}
            </h2>
          </div>

          {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-right">
                {error}
              </div>
          )}

          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
                  <div className="mb-4">
                    <label className="block text-right mb-1 text-sm font-medium text-gray-700">
                      نام استان
                    </label>
                    <input
                        className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="مثال: تهران"
                        disabled={isBusy}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-right mb-1 text-sm font-medium text-gray-700">
                      کد استان
                    </label>
                    <input
                        className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="مثال: THR"
                        disabled={isBusy}
                    />
                  </div>
                </div>

                <div
                    className="w-full lg:w-56 flex flex-col gap-3 border-t lg:border-t-0 lg:border-r pt-3 lg:pt-0 lg:pr-4">
                  <ToggleSwitch
                      checked={isActive}
                      onChange={setIsActive}
                      disabled={isBusy}
                      label="فعال"
                  />
                  <ToggleSwitch
                      checked={isCustomerAvailable}
                      onChange={setIsCustomerAvailable}
                      disabled={isBusy}
                      label="مخصوص مشتری"
                  />
                  {isEdit && (
                      <Button
                          variant={"danger"}
                          onClick={handleDeleteProvince}
                          children={"حذف استان"}
                          disabled={isBusy}
                      />
                  )}
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-800">شهرها</h3>
                <Button
                    variant={"primary"}
                    onClick={handleAddCity}
                    disabled={isBusy}
                    children={"+ افزودن شهر"}
                />
              </div>

              <div className="space-y-3">
                {cities.length === 0 ? (
                    <p className="text-center text-gray-500 py-4 text-sm">
                      هنوز شهری اضافه نشده است
                    </p>
                ) : (
                    cities.map((city, index) => {
                      const isDeletingThisCity = deletingCityId === city.id;

                      return (
                          <div key={city.id || index} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex flex-col lg:flex-row gap-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
                                <input
                                    className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-500"
                                    value={city.name}
                                    onChange={(e) =>
                                        handleCityChange(index, "name", e.target.value)
                                    }
                                    placeholder="نام شهر"
                                    disabled={isBusy}
                                />
                                <input
                                    className="w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                                    value={city.code || ""}
                                    onChange={(e) =>
                                        handleCityChange(index, "code", e.target.value)
                                    }
                                    placeholder="کد شهر"
                                    disabled={isBusy}
                                />
                                <input
                                    className="w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                                    value={city.latitude || ""}
                                    onChange={(e) =>
                                        handleCityChange(index, "latitude", e.target.value)
                                    }
                                    placeholder="عرض جغرافیایی"
                                    disabled={isBusy}
                                />
                                <input
                                    className="w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                                    value={city.longitude || ""}
                                    onChange={(e) =>
                                        handleCityChange(index, "longitude", e.target.value)
                                    }
                                    placeholder="طول جغرافیایی"
                                    disabled={isBusy}
                                />
                                <input
                                    className="w-full border rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500"
                                    value={city.zoom || ""}
                                    onChange={(e) =>
                                        handleCityChange(index, "zoom", e.target.value)
                                    }
                                    placeholder="درجه بزرگنمایی"
                                    disabled={isBusy}
                                />
                              </div>

                              <div
                                  className="w-full lg:w-56 flex flex-col gap-3 border-t lg:border-t-0 lg:border-r pt-3 lg:pt-0 lg:pr-4">
                                <ToggleSwitch
                                    checked={city.is_active}
                                    onChange={(value) =>
                                        handleCityChange(index, "is_active", value)
                                    }
                                    disabled={isBusy}
                                    label="فعال"
                                />
                                <ToggleSwitch
                                    checked={city.is_customer_available ?? true}
                                    onChange={(value) =>
                                        handleCityChange(index, "is_customer_available", value)
                                    }
                                    disabled={isBusy}
                                    label="مخصوص مشتری"
                                />

                                <Button
                                    variant={"danger"}
                                    onClick={() => handleRemoveCity(index)}
                                    children={isDeletingThisCity ? "در حال حذف..." : "حذف شهر"}
                                    disabled={isBusy}
                                />
                              </div>
                            </div>
                          </div>
                      );
                    })
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button
                variant={"secondary"}
                onClick={onClose}
                children={"انصراف"}
                disabled={isBusy}
            />
            <Button
                variant={"primary"}
                onClick={handleSubmit}
                children={loading ? "در حال ذخیره..." : "ذخیره"}
                disabled={isBusy}
            />
          </div>
        </div>
      </div>
  );
}
